// Self-service schedule ("My schedule"). Lists the authenticated user's own
// upcoming shifts (self.read_schedule). Site names are resolved best-effort via
// useSites(); if the viewer can't list sites the table falls back to "Site #n".

import { useMemo } from 'react';
import type { Assignment, AssignmentStatus } from '@dsr/shared-types';
import { useMySchedule } from '../api/queries/self';
import { useSites } from '../api/queries/sites';
import { ApiError } from '../api/client';
import { fmtDateTime } from '../utils/date';
import { Badge, Table, type BadgeTone } from '../components/ui';
import './scheduling.css';

const STATUS_LABEL: Record<AssignmentStatus, string> = {
  open: 'Open',
  assigned: 'Assigned',
  confirmed: 'Confirmed',
  in_progress: 'In progress',
  completed: 'Completed',
  no_show: 'No show',
  canceled: 'Canceled',
};

// Assignment status → shared Badge tone (colors match the old sch-badge-* set).
const STATUS_TONE: Record<AssignmentStatus, BadgeTone> = {
  open: 'neutral',
  assigned: 'info',
  confirmed: 'info',
  in_progress: 'success',
  completed: 'neutral',
  no_show: 'danger',
  canceled: 'neutral',
};

export function MySchedulePage(): JSX.Element {
  const { data, isPending, isError, error } = useMySchedule();
  const sitesQuery = useSites();

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sitesQuery.data?.sites ?? []) m.set(s.id, s.name);
    return m;
  }, [sitesQuery.data]);

  if (isPending) {
    return <div className="page-state">Loading your schedule…</div>;
  }
  if (isError || !data) {
    return (
      <div className="page-state page-error" role="alert">
        {error instanceof ApiError ? error.message : 'Failed to load your schedule.'}
      </div>
    );
  }

  const now = Date.now();
  const upcoming: Assignment[] = [...data.assignments]
    .filter((a) => a.ends_at >= now)
    .sort((a, b) => a.starts_at - b.starts_at);

  return (
    <section className="page my-schedule-page">
      <header className="page-header">
        <div>
          <h1>My schedule</h1>
          <p className="page-sub">Your upcoming assigned shifts.</p>
        </div>
      </header>

      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell>Site</Table.HeaderCell>
            <Table.HeaderCell>Post</Table.HeaderCell>
            <Table.HeaderCell>Start</Table.HeaderCell>
            <Table.HeaderCell>End</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {upcoming.length === 0 ? (
            <Table.Empty colSpan={5}>No upcoming shifts on your schedule.</Table.Empty>
          ) : (
            upcoming.map((a) => (
              <Table.Row key={a.id}>
                <Table.Cell tone="strong">{siteName.get(a.site_id) ?? `Site #${a.site_id}`}</Table.Cell>
                <Table.Cell tone="muted">{a.post_id != null ? `Post #${a.post_id}` : '—'}</Table.Cell>
                <Table.Cell>{fmtDateTime(a.starts_at)}</Table.Cell>
                <Table.Cell>{fmtDateTime(a.ends_at)}</Table.Cell>
                <Table.Cell>
                  <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>
    </section>
  );
}
